import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { CoachListGrid } from "~/components/coach-list-grid";
import { FaqList } from "~/components/faq-list";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { ExploreMoreLinks } from "~/components/feature/explore-more-links";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { MarkdownContent } from "~/components/markdown-content";
import { ScrollingTagList } from "~/components/scrolling-tag-list";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { CtaBanner } from "~/components/ui/cta-banner";
import { Heading } from "~/components/ui/heading";
import { Subheading } from "~/components/ui/subheading";
import { getCityLabelFromValue } from "~/lib/functions/cities";
import { getCityPageContent } from "~/lib/functions/coach-group-seo";
import { getCategoriesInCityWithCoaches } from "~/lib/functions/expertise";
import { coachQueryOptions } from "~/lib/query-options";
import type { CityCategoryOption } from "~/lib/server/expertise";
import { coachListJsonLdScripts } from "~/lib/shared/coach-list-head";
import { MIN_COACHES_FOR_LISTING } from "~/lib/shared/coach-listing-thresholds";
import { createBrandMeta, createNoIndexMeta } from "~/lib/shared/meta";

// TODO: add cacheControl middleware to TanStack Start's global middleware

// The category list is alphabetical; the strongest hub is the one with the
// most visible coaches in this city (first alphabetically on a tie).
function findStrongestCategory(
  categories: CityCategoryOption[],
): CityCategoryOption | undefined {
  return categories.reduce<CityCategoryOption | undefined>(
    (strongest, category) =>
      strongest && strongest.coachCount >= category.coachCount
        ? strongest
        : category,
    undefined,
  );
}

export const Route = createFileRoute("/_public/coaches/city/$slug/")({
  loader: async ({ context, params }) => {
    const citySlug = params.slug;

    // Pre-load data into React Query and also fetch for head function
    const [_coaches, _categories, page, cityLabel] = await Promise.all([
      context.queryClient.ensureQueryData(coachQueryOptions.byCity(citySlug)),
      context.queryClient.ensureQueryData({
        queryKey: ["categories", "city", citySlug],
        queryFn: () => getCategoriesInCityWithCoaches({ data: { citySlug } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["cityPageContent", citySlug],
        queryFn: () => getCityPageContent({ data: { citySlug } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["cityLabel", citySlug],
        queryFn: () => getCityLabelFromValue({ data: { citySlug } }),
      }),
    ]);

    // A slug that isn't in the city catalog has no label, no coaches, and no
    // reason to exist — 404 instead of rendering a "coaches in this city"
    // template for an infinite URL space.
    if (!cityLabel) {
      throw notFound();
    }

    return {
      page,
      cityLabel,
      coachCount: _coaches.length,
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      // Loader threw notFound — never emit a canonical or indexable meta here.
      return createNoIndexMeta(
        "Professional Coaches | Dream Coach Match",
        "Find professional coaches in your area on Dream Coach Match.",
      );
    }

    const { page, cityLabel, coachCount } = loaderData;
    const name = cityLabel;
    const citySlug = params.slug;

    const title =
      page?.seoTitle ?? `Professional Coaches in ${name} | Dream Coach Match`;
    const description =
      page?.metaDescription ??
      `Dream Coach Match makes it easy to find life coaches, health coaches, business and executive coaches in ${name} — helping you find the right fit. Many offer virtual sessions or in-person coaching in ${name}.`;

    const metaConfig = createBrandMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogType: "website",
      ogUrl: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
      twitterCard: "summary_large_image",
      twitterTitle: `Find Professional Coaches in ${name} | Dream Coach Match`,
      twitterDescription: `Discover life coaches, business coaches, and health coaches in ${name}. Connect with the right coach for your personal and professional development goals.`,
      canonical: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
      // Any city page worth listing in on-site navigation is worth indexing
      // (founder decision, Aug 2026). Below the listing threshold the page is
      // a dead end: noindex it but let crawlers follow through to the coach
      // profiles. The sitemap uses the same cutoff so both signals agree.
      noindexFollow: coachCount < MIN_COACHES_FOR_LISTING,
      keywords: [
        "coaches",
        name,
        "life coach",
        "business coach",
        "health coach",
        "executive coach",
        "professional coaching",
        `coaches in ${name}`,
        `${name} coaching`,
        "local coaches",
        "find coach",
      ],
      author: "Dream Coach Match",
    });

    const pageUrl = `https://dreamcoachmatch.com/coaches/city/${citySlug}`;

    const collectionPageStructuredData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `Professional Coaches in ${name}`,
      description: description,
      url: pageUrl,
      mainEntity: {
        "@type": "ItemList",
        name: `Coaches in ${name}`,
        description: `Directory of professional coaches available in ${name}`,
        itemListOrder: "https://schema.org/ItemListUnordered",
      },
    };

    return {
      ...metaConfig,
      scripts: coachListJsonLdScripts({
        collectionPage: collectionPageStructuredData,
        breadcrumbs: [
          { name: "Home", url: "https://dreamcoachmatch.com" },
          { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
          { name: "Cities", url: "https://dreamcoachmatch.com/coaches/city" },
          { name, url: pageUrl },
        ],
        faqItems: page?.faqItems,
      }),
    };
  },
  component: CityCoachesPage,
});

function CityCoachesPage() {
  const { slug: citySlug } = Route.useParams();

  const { data: coaches = [] } = useQuery(coachQueryOptions.byCity(citySlug));

  const { data: activeCategories = [] } = useQuery({
    queryKey: ["categories", "city", citySlug],
    queryFn: () => getCategoriesInCityWithCoaches({ data: { citySlug } }),
  });

  const { data: page } = useQuery({
    queryKey: ["cityPageContent", citySlug],
    queryFn: () => getCityPageContent({ data: { citySlug } }),
  });

  const { data: cityLabel } = useQuery({
    queryKey: ["cityLabel", citySlug],
    queryFn: () => getCityLabelFromValue({ data: { citySlug } }),
  });

  if (!coaches) {
    // TODO: handle 404 case properly - throw notFound() in loader instead
    return <div>Coaches for this city not found</div>;
  }

  const name = cityLabel || citySlug;
  // Prefer SEO content from page, fallback to current text
  const headline =
    page?.seoTitle || `Looking for a professional coach in ${name}?`;
  const subheadline =
    page?.metaDescription ||
    `Dream Coach Match makes it easy to find life coaches, health coaches, business and executive coaches in ${name} — whether you're seeking clarity, growth, or support during a major transition.`;
  const intro1 = `Each coach profile includes key details about their background, coaching style, and areas of focus — helping you find the right fit for your goals. Many coaches on Dream Coach Match offer virtual sessions, and several are available for in-person coaching in ${name}.`;
  const intro2 = `Browse coaches available in ${name} and discover the perfect match for your goals, challenges, and aspirations.`;

  const topCategoryNames = activeCategories
    .slice(0, 5)
    .map((cat) => cat.listingName ?? cat.name);

  const { onlineCount, inPersonCount } = coaches.reduce(
    (counts, coach) => {
      const methods = coach.sessionDeliveryMethods ?? [];
      if (methods.includes("online") || methods.includes("hybrid")) {
        counts.onlineCount += 1;
      }
      if (methods.includes("in-person") || methods.includes("hybrid")) {
        counts.inPersonCount += 1;
      }
      return counts;
    },
    { onlineCount: 0, inPersonCount: 0 },
  );

  const languages = [
    ...new Set(
      coaches
        .flatMap((coach) => coach.languages?.split(/[,;/&]/) ?? [])
        .map((lang) => lang.trim())
        .filter(Boolean),
    ),
  ].sort();

  const sessionParts: string[] = [];
  if (onlineCount > 0) {
    sessionParts.push(
      `${onlineCount} ${onlineCount === 1 ? "coach offers" : "coaches offer"} online sessions`,
    );
  }
  if (inPersonCount > 0) {
    sessionParts.push(
      `${inPersonCount} available for in-person sessions in ${name}`,
    );
  }

  const hasSignals =
    topCategoryNames.length > 0 ||
    sessionParts.length > 0 ||
    languages.length > 0;

  const strongestCategory = findStrongestCategory(activeCategories);
  const exploreMoreLinks = [
    { to: "/how-it-works", label: "How It Works" },
    { to: "/wheel-of-life", label: "Wheel of Life" },
    { to: "/coaches/city", label: "All Cities" },
    ...(strongestCategory
      ? [
          {
            to: `/coaches/category/${strongestCategory.slug}`,
            label: strongestCategory.listingName || strongestCategory.name,
          },
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <Breadcrumb
        className="justify-center"
        items={[
          { label: "Home", href: "/" },
          { label: "Coaches", href: "/coaches" },
          { label: "Cities", href: "/coaches/city" },
          { label: name },
        ]}
      />
      <Heading headline={headline} subheadline={subheadline} isH1 />
      <BreakdownIntro
        breakdownName={name}
        breakdownTypeSlug={citySlug}
        introTextLine1={intro1}
        introTextLine2={intro2}
        ctaButtonTextTemplate="Find Your Coach"
        utmCampaignPrefix="city_coaches"
      />
      {activeCategories && activeCategories.length > 0 && (
        <div className="my-12">
          <Subheading className="text-center">
            {`What kind of coaching are you looking for in ${name}?`}
          </Subheading>
          <div className="mt-6">
            <ScrollingTagList
              items={activeCategories.map((cat) => ({
                id: cat.id,
                name: cat.name,
                listingName:
                  cat.listingName === null ? undefined : cat.listingName,
                slug: cat.slug,
              }))}
              baseHref={`/coaches/city/${citySlug}/category/`}
            />
          </div>
        </div>
      )}
      {hasSignals && (
        <section className="my-12 rounded-lg border border-gray-200 bg-gray-50 p-6 sm:p-8">
          <Subheading className="text-xl">
            {`What coaching looks like in ${name}`}
          </Subheading>
          <dl className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            {topCategoryNames.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Top focus areas
                </dt>
                <dd className="mt-1 text-base text-gray-900">
                  {topCategoryNames.join(", ")}
                </dd>
              </div>
            )}
            {sessionParts.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Session formats
                </dt>
                <dd className="mt-1 text-base text-gray-900">
                  {sessionParts.join(" · ")}
                </dd>
              </div>
            )}
            {languages.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Languages spoken
                </dt>
                <dd className="mt-1 text-base text-gray-900">
                  {languages.join(", ")}
                </dd>
              </div>
            )}
          </dl>
        </section>
      )}
      <Subheading className="mt-12 text-center">
        {`Coaches in ${name}`}
      </Subheading>
      <CoachListGrid
        className="mt-6 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3"
        coaches={coaches}
        emptyMessage="No coaches found for this city yet. Check back soon!"
        midListCta={
          <MidListCTA
            breakdownName={name}
            breakdownTypeSlug={citySlug}
            preLinkText="Not sure which coach to choose?"
            ctaLinkTextTemplate="Try our free Coach Matching Tool!"
            utmCampaignPrefix="city_coaches_mid"
          />
        }
      />
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={name}
        breakdownTypeSlug={citySlug}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate={`Ready to connect with your dream coach in ${name}?`}
        mainCtaButtonTextTemplate="Find Your Dream Coach"
        utmCampaignPrefix="city_coaches_bottom"
      />
      <ExploreMoreLinks links={exploreMoreLinks} />
      {page?.contentMd && (
        <div className="mx-auto prose mt-12">
          <MarkdownContent content={page.contentMd} demoteH1 />
        </div>
      )}
      <div className="mx-auto mt-12 max-w-prose">
        <CtaBanner
          headline={`Ready to connect with your dream coach in ${name}?`}
          buttonText="Find Your Dream Coach"
          buttonHref={`/discovery?utm_campaign=city_coaches_bottom&utm_source=${citySlug}`}
        >
          <div className="mt-4">
            <a
              href="/page/member"
              className="my-2 text-center text-xs text-purple-200/70 underline transition-colors hover:text-white hover:no-underline"
            >
              How does Dream Coach Match work?
            </a>
          </div>
        </CtaBanner>
      </div>
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}
